import { DataGranularity } from "@flexidao/dto";
import {
    formatDate,
    formatDateHour,
    formatDateTime,
    formatMonth,
    physicalRoundForElectricity,
} from "@flexidao/ui-lib/utils";
import { DAY_MS } from "@flexidao/ui-lib/consts";
import { TickFormatter } from "@visx/axis";
import { ScaleInput, ScaleTypeToD3Scale } from "@visx/scale";
import { timeFormat, utcFormat } from "d3-time-format";
import { AM_LABEL_HOUR, PM_LABEL_HOUR } from "./_utils/utils";

const fullTimeFormatSpecifier = "%H:%M:%S";

export const getDayHourDateRangeFormat = (startTime: Date, endTime: Date): string => {
    return (
        utcFormat("%d/%m/%Y %H:%M:%S")(startTime) + " - " + utcFormat("%d/%m/%Y %H:%M:%S")(endTime)
    );
};
export const getDayDateRangeFormat = (startTime: Date, endTime: Date): string => {
    return utcFormat("%d/%m/%Y")(startTime) + " - " + utcFormat("%d/%m/%Y")(endTime);
};

export const getOnlyHourRangeFormat = (startTime: Date, endTime: Date): string => {
    return (
        utcFormat(fullTimeFormatSpecifier)(startTime) +
        " - " +
        utcFormat(fullTimeFormatSpecifier)(endTime)
    );
};

export const dateHourTickFormatter = (
    tick: Date | { valueOf(): number },
    index: number,
    values: {
        value: Date | { valueOf(): number };
        index: number;
    }[],
): string => {
    const length = values.length;
    if (index === 0) {
        return "";
    }
    if (index === length - 1) {
        return "24h";
    }

    const ms = tick instanceof Date ? tick : new Date(tick.valueOf());
    return timeFormat("%H h")(ms);
};

export const hourTickFormatter = (tick: Date | { valueOf(): number }, index: number): string => {
    if (index === 0) {
        return "";
    }

    const ms = tick instanceof Date ? tick : new Date(tick.valueOf());
    return timeFormat("%d %b %H h")(ms);
};
export const dayTickFormatter = (tick: Date | { valueOf(): number }, _: number): string => {
    //if (index === 0) return "";

    const ms = tick instanceof Date ? tick : new Date(tick.valueOf());
    return timeFormat("%d %b")(ms);
};

// eslint-disable-next-line no-magic-numbers
const THREE_DAYS_MS = 3 * DAY_MS;

export const getXTickFormatter =
    (minStartDate: Date | null, maxEndDate: Date | null, granularity?: DataGranularity) =>
    (date_: Date | { valueOf(): number }): string => {
        if (!minStartDate || !maxEndDate) {
            return "-";
        }

        const minStartTimeMs: number = minStartDate.getTime() ?? 0;
        const maxEndTimeMs: number = maxEndDate.getTime() ?? 0;

        const isLessThan1Day: boolean = maxEndTimeMs - minStartTimeMs < DAY_MS;
        const isLessThan3Days: boolean = maxEndTimeMs - minStartTimeMs < THREE_DAYS_MS;

        const date: Date = date_ instanceof Date ? date_ : new Date(date_.valueOf());

        if ((granularity == null || granularity === DataGranularity.Hourly) && isLessThan1Day) {
            return formatDateTime(date, "UTC");
        }

        if ((granularity == null || granularity === DataGranularity.Hourly) && isLessThan3Days) {
            return formatDateHour(date, "UTC");
        }

        return formatDate(date, "UTC");
    };

export const getTooltipDateFormatter =
    (granularity: DataGranularity) =>
    (date_: Date | { valueOf(): number }): string => {
        const date: Date = date_ instanceof Date ? date_ : new Date(date_.valueOf());

        switch (granularity) {
            case DataGranularity.Hourly:
                return formatDateTime(date, "UTC");
            case DataGranularity.Daily:
                return formatDate(date, "UTC");
            case DataGranularity.Monthly:
                return formatMonth(date, "UTC");
        }
    };

export const dateTickFormatter =
    (minStartDate: Date, maxEndDate: Date): TickFormatter<ScaleInput<ScaleTypeToD3Scale["time"]>> =>
    (tick: Date | { valueOf(): number }): string => {
        const ms = tick instanceof Date ? tick : new Date(tick.valueOf());
        const isLessThan3Days = maxEndDate.getTime() - minStartDate.getTime() < THREE_DAYS_MS;
        if (isLessThan3Days) {
            return timeFormat("%d %b %H h")(ms);
        } else {
            return timeFormat("%d %b")(ms);
        }
    };

export const wattsTickFormatter: TickFormatter<ScaleInput<ScaleTypeToD3Scale["linear"]>> = (
    tick: number | { valueOf(): number },
): string => {
    const v = typeof tick === "number" ? tick : tick.valueOf();
    const decimals = 2;
    const [value, magnitude] = physicalRoundForElectricity(v, decimals);
    return value + " " + magnitude + "W";
};

export const energyTickFormatter: TickFormatter<ScaleInput<ScaleTypeToD3Scale["linear"]>> = (
    tick: number | { valueOf(): number },
): string => {
    const v = typeof tick === "number" ? tick : tick.valueOf();
    const [value, magnitude] = physicalRoundForElectricity(v, 0);
    return value + " " + magnitude + "Wh";
};

const LAST_DAY_JANUARY = 31;
const LAST_DAY_FEBRUARY = 59;
const LAST_DAY_MARCH = 90;
const LAST_DAY_APRIL = 120;
const LAST_DAY_MAY = 151;
const LAST_DAY_JUNE = 181;
const LAST_DAY_JULY = 211;
const LAST_DAY_AUGUST = 242;
const LAST_DAY_SEPTEMBER = 272;
const LAST_DAY_OCTOBER = 303;
const LAST_DAY_NOVEMBER = 333;

export const heatmapDaysToMonthTickFormatter = (tick_: number | { valueOf(): number }): string => {
    const tick = typeof tick_ === "number" ? tick_ : tick_.valueOf();

    if (tick >= 0 && tick <= LAST_DAY_JANUARY) {
        return "Jan";
    } else if (tick > LAST_DAY_JANUARY && tick <= LAST_DAY_FEBRUARY) {
        return "Feb";
    } else if (tick > LAST_DAY_FEBRUARY && tick <= LAST_DAY_MARCH) {
        return "Mar";
    } else if (tick > LAST_DAY_MARCH && tick <= LAST_DAY_APRIL) {
        return "Apr";
    } else if (tick > LAST_DAY_APRIL && tick <= LAST_DAY_MAY) {
        return "May";
    } else if (tick > LAST_DAY_MAY && tick <= LAST_DAY_JUNE) {
        return "Jun";
    } else if (tick > LAST_DAY_JUNE && tick <= LAST_DAY_JULY) {
        return "Jul";
    } else if (tick > LAST_DAY_JULY && tick <= LAST_DAY_AUGUST) {
        return "Aug";
    } else if (tick > LAST_DAY_AUGUST && tick <= LAST_DAY_SEPTEMBER) {
        return "Sep";
    } else if (tick > LAST_DAY_SEPTEMBER && tick <= LAST_DAY_OCTOBER) {
        return "Oct";
    } else if (tick > LAST_DAY_OCTOBER && tick <= LAST_DAY_NOVEMBER) {
        return "Nov";
    } else {
        return "Dec";
    }
};

export const heatmapPartOfTheDayTickFormatter = (tick: number | { valueOf(): number }): string => {
    if (tick === AM_LABEL_HOUR) {
        return "AM";
    }
    if (tick === PM_LABEL_HOUR) {
        return "PM";
    }
    return "";
};
